import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search, Users } from 'lucide-react';
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier } from '../services/api';
import './SuppliersPage.css';

function SuppliersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contactName: '',
    email: '',
    phone: '',
    taxId: '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const showFeedback = useCallback((type, message) => {
    setFeedback({ type, message });
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      const response = await getSuppliers();
      setSuppliers(response.suppliers || []);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (supplier = null) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setFormData({
        name: supplier.name,
        contactName: supplier.contactName || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        taxId: supplier.taxId || '',
      });
    } else {
      setEditingSupplier(null);
      setFormData({
        name: '',
        contactName: '',
        email: '',
        phone: '',
        taxId: '',
      });
    }
    setFormErrors({});
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.name.trim()) errors.name = t('customers.validation.nameRequired');
    if (formData.taxId && !/^\d{2}-\d{8}-\d$/.test(formData.taxId)) {
      errors.taxId = t('suppliers.invalidTaxId');
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    try {
      if (editingSupplier) {
        await updateSupplier(editingSupplier.supplierId, formData);
      } else {
        await createSupplier(formData);
      }
      setModalOpen(false);
      loadSuppliers();
      showFeedback('success', t('suppliers.saveSuccess'));
    } catch (error) {
      console.error('Error saving supplier:', error);
      showFeedback('error', t('suppliers.saveError'));
    }
  };

  const handleDelete = async (e, supplierId) => {
    e.stopPropagation(); // Prevent navigating to details
    if (window.confirm(t('common.confirmDelete'))) {
      try {
        await deleteSupplier(supplierId);
        loadSuppliers();
        showFeedback('success', t('suppliers.deleteSuccess'));
      } catch (error) {
        console.error('Error deleting supplier:', error);
        showFeedback('error', t('suppliers.deleteError'));
      }
    }
  };

  const handleEdit = (e, supplier) => {
    e.stopPropagation(); // Prevent navigating to details
    handleOpenModal(supplier);
  };

  const filteredSuppliers = useMemo(() => {
    if (!searchQuery.trim()) return suppliers;
    const query = searchQuery.toLowerCase();
    return suppliers.filter(
      (s) =>
        s.name.toLowerCase().includes(query) ||
        (s.contactName && s.contactName.toLowerCase().includes(query)) ||
        (s.email && s.email.toLowerCase().includes(query)) ||
        (s.phone && s.phone.includes(query))
    );
  }, [suppliers, searchQuery]);

  if (loading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="suppliers-page">
      <div className="page-header">
        <h1>{t('suppliers.title', 'Proveedores')}</h1>
        <button type="button" className="btn btn-primary" onClick={() => handleOpenModal()}>
          {t('suppliers.addSupplier', 'Agregar Proveedor')}
        </button>
      </div>

      {feedback && (
        <div
          className={`alert alert-${feedback.type === 'error' ? 'error' : 'success'}`}
          role="alert"
          aria-live="polite"
        >
          {feedback.message}
        </div>
      )}

      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder={t('suppliers.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="card">
        {filteredSuppliers.length === 0 ? (
          <div className="empty-state">
            <Users size={48} />
            <p>{t('suppliers.emptyState')}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="suppliers-table">
              <thead>
                <tr>
                  <th>{t('suppliers.name', 'Nombre')}</th>
                  <th>{t('suppliers.contact', 'Contacto')}</th>
                  <th>{t('suppliers.phone', 'Teléfono')}</th>
                  <th className="col-email">{t('suppliers.email', 'Email')}</th>
                  <th className="col-taxid">{t('suppliers.taxId', 'CUIT/RUT')}</th>
                  <th>{t('suppliers.balance', 'Saldo')}</th>
                  <th>{t('common.actions', 'Acciones')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredSuppliers.map((supplier) => (
                  <tr
                    key={supplier.supplierId}
                    onClick={() => navigate(`/inventory/suppliers/${supplier.supplierId}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td data-label={t('suppliers.name', 'Nombre')}>{supplier.name}</td>
                    <td data-label={t('suppliers.contact', 'Contacto')}>{supplier.contactName}</td>
                    <td data-label={t('suppliers.phone', 'Teléfono')}>{supplier.phone}</td>
                    <td
                      data-label={t('suppliers.email', 'Email')}
                      className="col-email"
                      title={supplier.email}
                    >
                      {supplier.email}
                    </td>
                    <td data-label={t('suppliers.taxId', 'CUIT/RUT')} className="col-taxid">
                      {supplier.taxId}
                    </td>
                    <td
                      data-label={t('suppliers.balance', 'Saldo')}
                      className={`col-balance ${supplier.balance < 0 ? 'text-danger' : 'text-success'}`}
                    >
                      ${parseFloat(supplier.balance || 0).toFixed(2)}
                    </td>
                    <td data-label={t('common.actions', 'Acciones')}>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={(e) => handleEdit(e, supplier)}
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={(e) => handleDelete(e, supplier.supplierId)}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>
                {editingSupplier
                  ? t('suppliers.editSupplier', 'Editar Proveedor')
                  : t('suppliers.newSupplier', 'Nuevo Proveedor')}
              </h2>
              <button
                type="button"
                className="close-btn"
                onClick={() => setModalOpen(false)}
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit} noValidate>
              <div className="modal-body">
                <p className="required-legend">* {t('common.requiredFieldsLegend')}</p>
                <div className={`form-group${formErrors.name ? ' has-error' : ''}`}>
                  <label>{t('suppliers.name', 'Nombre')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      if (formErrors.name) setFormErrors((prev) => ({ ...prev, name: '' }));
                      setFormData({ ...formData, name: e.target.value });
                    }}
                    aria-required="true"
                    aria-invalid={!!formErrors.name}
                  />
                  {formErrors.name && (
                    <span className="field-error" role="alert">
                      {formErrors.name}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>{t('suppliers.contact', 'Nombre de Contacto')}</label>
                  <input
                    type="text"
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('suppliers.phone', 'Teléfono')}</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+54 11 1234-5678"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('suppliers.email', 'Email')}</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>
                <div className={`form-group${formErrors.taxId ? ' has-error' : ''}`}>
                  <label>{t('suppliers.taxId', 'CUIT/RUT')}</label>
                  <input
                    type="text"
                    value={formData.taxId}
                    maxLength={13}
                    placeholder="XX-XXXXXXXX-X"
                    aria-invalid={!!formErrors.taxId}
                    onChange={(e) => {
                      if (formErrors.taxId) setFormErrors((prev) => ({ ...prev, taxId: '' }));
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
                      let formatted = digits;
                      if (digits.length > 2) formatted = digits.slice(0, 2) + '-' + digits.slice(2);
                      if (digits.length > 10)
                        formatted =
                          digits.slice(0, 2) + '-' + digits.slice(2, 10) + '-' + digits.slice(10);
                      setFormData({ ...formData, taxId: formatted });
                    }}
                  />
                  {formErrors.taxId && (
                    <span className="field-error" role="alert">
                      {formErrors.taxId}
                    </span>
                  )}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setModalOpen(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuppliersPage;
